<app-page-header [title]="greeting()">
  <span [vRequiredRoles]="['incidents.manager']">
    <app-create-incident-button icon="plus">
      {{ 'pages.incidentList.createIncidentBtn' | transloco }}
    </app-create-incident-button>
  </span>
</app-page-header>

<div class="mt-8 flex flex-col gap-6">
  <!-- Active incidents -->
  <vx-card>
    <vx-card-header
      [title]="'pages.dashboard.activeIncidents.title' | transloco">
      <vx-icon-button
        vxCardHeaderActions
        icon="dots-three"
        variant="ghost"
        size="sm"
        tone="secondary"
        [ariaLabel]="'pages.dashboard.activeIncidents.menu' | transloco"
        [vxMenuTriggerFor]="incidentsMenu" />
      <ng-template #incidentsMenu>
        <vx-menu>
          <vx-menu-item icon="fire" (triggered)="goTo(['/incidents'])">
            {{ 'pages.dashboard.activeIncidents.viewAll' | transloco }}
          </vx-menu-item>
          <span [vRequiredRoles]="['incidents.manager']">
            <vx-menu-item icon="plus" (triggered)="newIncident()">
              {{ 'pages.incidentList.createIncidentBtn' | transloco }}
            </vx-menu-item>
          </span>
        </vx-menu>
      </ng-template>
    </vx-card-header>

    @if (incidentsSkeleton()) {
      <vx-panel variant="list">
        @for (placeholder of incidentPlaceholders; track $index) {
          <div
            class="flex h-17 items-center gap-6 border-b border-zinc-750 bg-zinc-800 px-4.5 last:border-b-0">
            <vx-skeleton class="size-8 rounded-lg" />
            <vx-skeleton class="h-3.5 w-48" />
            <vx-skeleton class="ml-auto h-3.5 w-24" />
          </div>
        }
      </vx-panel>
    } @else if (incidentsEmpty()) {
      <vx-empty-state
        variant="glow"
        icon="fire"
        [title]="'pages.dashboard.activeIncidents.empty.title' | transloco"
        [description]="
          'pages.dashboard.activeIncidents.empty.description' | transloco
        " />
    } @else {
      <vx-panel variant="list">
        @for (row of incidents(); track row.id) {
          <vx-list-row
            [link]="incidentLink(row)"
            columnTemplate="minmax(0,2fr) minmax(0,1fr) 7rem 5.5rem">
            <vx-item-cell [title]="row.title" [subtitle]="row.summary">
              <span
                vxItemCellLeading
                class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-zinc-750">
                <vx-icon name="fire" class="text-zinc-200" />
              </span>
            </vx-item-cell>
            <app-incident-status-label
              [state]="row.state"
              [states]="row.states" />
            <span class="text-sm tracking-tight text-zinc-400">
              {{ row.createdAt | dateAgo }}
            </span>
            <span class="flex justify-end">
              @if (row.priority) {
                <app-incident-priority-label [priority]="row.priority" />
              }
            </span>
          </vx-list-row>
        }
      </vx-panel>
    }
  </vx-card>

  <!-- Monitors -->
  <vx-data-table
    [columns]="monitorColumns()"
    [rows]="monitors()"
    rowKey="id"
    [rowLink]="monitorLink"
    [loading]="monitorsLoading()"
    emptyIcon="pulse"
    [emptyTitle]="'pages.dashboard.monitors.empty.title' | transloco"
    [emptyDescription]="
      'pages.dashboard.monitors.empty.description' | transloco
    ">
    <vx-icon-button
      vxTableActions
      icon="dots-three"
      variant="ghost"
      size="sm"
      tone="secondary"
      [ariaLabel]="'pages.dashboard.monitors.menu' | transloco"
      [vxMenuTriggerFor]="monitorsMenu" />
    <ng-template #monitorsMenu>
      <vx-menu>
        <vx-menu-item icon="pulse" (triggered)="goTo(['/monitors'])">
          {{ 'pages.dashboard.monitors.viewAll' | transloco }}
        </vx-menu-item>
        <span [vRequiredRoles]="['monitors.manager']">
          <vx-menu-item icon="plus" (triggered)="goTo(['/monitors/new'])">
            {{ 'pages.createMonitor.createMonitorBtn' | transloco }}
          </vx-menu-item>
        </span>
      </vx-menu>
    </ng-template>

    <ng-template vxHeader="monitor">{{
      'resources.monitor.singular' | transloco
    }}</ng-template>
    <ng-template vxHeader="status">{{
      'pages.monitorsList.table.status' | transloco
    }}</ng-template>
    <ng-template vxHeader="uptime">{{
      'pages.monitorsList.table.uptime' | transloco
    }}</ng-template>
    <ng-template vxCell="monitor" [vxCellOf]="monitors()" let-row>
      <vx-item-cell
        [icon]="row.icon"
        [title]="row.name"
        [subtitle]="row.target" />
    </ng-template>
    <ng-template vxCell="status" [vxCellOf]="monitors()" let-row>
      <app-monitor-status-label [status]="row.status" />
    </ng-template>
    <ng-template vxCell="uptime" [vxCellOf]="monitors()" let-row>
      <app-monitor-sli-value
        [stats]="row.monitor.statistics?.availability"
        [status]="row.status" />
    </ng-template>
    <ng-template vxRowActions [vxRowActionsOf]="monitors()" let-row>
      <vx-icon-button
        icon="dots-three"
        variant="ghost"
        size="sm"
        tone="secondary"
        [ariaLabel]="'components.monitorRow.actions.menu' | transloco"
        [vxMenuTriggerFor]="monitorRowMenu" />
      <ng-template #monitorRowMenu>
        <vx-menu>
          <vx-menu-item icon="eye" (triggered)="goTo(monitorLink(row))">
            {{ 'components.monitorRow.actions.open' | transloco }}
          </vx-menu-item>
          <span [vRequiredRoles]="['monitors.manager']">
            <vx-menu-item
              [icon]="row.paused ? 'play' : 'pause'"
              (triggered)="setMonitorPaused(row)">
              {{
                (row.paused
                  ? 'components.monitorRow.actions.resume'
                  : 'components.monitorRow.actions.pause'
                ) | transloco
              }}
            </vx-menu-item>
          </span>
        </vx-menu>
      </ng-template>
    </ng-template>
  </vx-data-table>

  <!-- Services -->
  <vx-card>
    <vx-card-header [title]="'pages.dashboard.services.title' | transloco">
      <vx-icon-button
        vxCardHeaderActions
        icon="dots-three"
        variant="ghost"
        size="sm"
        tone="secondary"
        [ariaLabel]="'pages.dashboard.services.menu' | transloco"
        [vxMenuTriggerFor]="servicesMenu" />
      <ng-template #servicesMenu>
        <vx-menu>
          <vx-menu-item icon="diamonds-four" (triggered)="goTo(['/services'])">
            {{ 'pages.dashboard.services.viewAll' | transloco }}
          </vx-menu-item>
          <span [vRequiredRoles]="['services.manager']">
            <vx-menu-item icon="plus" (triggered)="goTo(['/services/new'])">
              {{ 'pages.serviceList.createServiceBtn' | transloco }}
            </vx-menu-item>
          </span>
        </vx-menu>
      </ng-template>
    </vx-card-header>

    @if (servicesSkeleton()) {
      <vx-panel variant="group">
        <div class="flex flex-wrap gap-2">
          @for (placeholder of servicePlaceholders; track $index) {
            <vx-skeleton class="h-8 w-28 rounded-full" />
          }
        </div>
      </vx-panel>
    } @else if (servicesEmpty()) {
      <vx-empty-state
        icon="diamonds-four"
        [title]="'pages.dashboard.services.empty.title' | transloco"
        [description]="
          'pages.dashboard.services.empty.description' | transloco
        " />
    } @else {
      <vx-panel variant="group">
        <div class="flex flex-wrap gap-2">
          @for (service of services(); track service.id) {
            <a
              class="flex items-center gap-2 rounded-full bg-zinc-800 px-3 py-1.5 text-sm tracking-tight text-zinc-200 inset-ring inset-ring-zinc-750 transition-colors duration-150 hover:bg-zinc-750 hover:text-zinc-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-50"
              [routerLink]="['/services', service.id]">
              <span
                class="size-2 shrink-0 rounded-full bg-zinc-500"
                [style.background-color]="service.color"
                aria-hidden="true"></span>
              {{ service.name }}
            </a>
          }
        </div>
      </vx-panel>
    }
  </vx-card>
</div>
